/* import __COLOCATED_TEMPLATE__ from './wfm-attribute-assigner.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { get, post } from 'embercom/lib/ajax';

interface WfmAttributeOption {
  id: string;
  value: string;
  position: number;
}

interface WfmAttribute {
  id: string;
  name: string;
  dataType: string;
  options: WfmAttributeOption[];
}

interface Signature {
  Args: {
    selectedTeammateIds: number[];
  };
}

export default class WfmAttributeAssigner extends Component<Signature> {
  @service declare appService: { app: { id: string } };
  @service declare intl: { t: (key: string, options?: Record<string, unknown>) => string };
  @service declare notificationsService: {
    notifyConfirmation: (msg: string) => void;
    notifyError: (msg: string) => void;
  };

  @tracked isMenuOpen = false;
  @tracked isModalOpen = false;
  @tracked attributes: WfmAttribute[] = [];
  @tracked selectedAttributeId: string | null = null;
  @tracked selectedOptionId: string | null = null;
  @tracked isLoading = false;
  @tracked isSaving = false;

  get selectedAttribute(): WfmAttribute | undefined {
    return this.attributes.find((a) => a.id === this.selectedAttributeId);
  }

  get canApply(): boolean {
    return this.selectedOptionId !== null && this.selectedOptionId !== '' && !this.isSaving;
  }

  @action
  async toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.isMenuOpen && this.attributes.length === 0) {
      this.isLoading = true;
      try {
        let response = await get(`/ember/wfm/teammate_attributes?app_id=${this.appService.app.id}`);
        this.attributes = response as WfmAttribute[];
      } catch {
        this.notificationsService.notifyError(
          this.intl.t('settings.teammate-attributes.load-error'),
        );
        this.isMenuOpen = false;
      }
      this.isLoading = false;
    }
  }

  @action
  openModal(attributeId: string) {
    this.selectedAttributeId = attributeId;
    this.selectedOptionId = null;
    this.isMenuOpen = false;
    this.isModalOpen = true;
  }

  @action
  closeModal() {
    this.isModalOpen = false;
    this.selectedAttributeId = null;
    this.selectedOptionId = null;
  }

  @action
  selectOption(optionId: string) {
    this.selectedOptionId = optionId;
  }

  @action
  async applyToSelected() {
    if (!this.canApply) {
      return;
    }
    this.isSaving = true;

    try {
      await post(`/ember/wfm/teammate_attribute_assignments?app_id=${this.appService.app.id}`, {
        admin_ids: this.args.selectedTeammateIds,
        teammate_attribute_id: Number(this.selectedAttributeId),
        value: this.selectedOptionId,
      });
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.teammate-attributes.update-success', {
          count: this.args.selectedTeammateIds.length,
        }),
      );
      this.closeModal();
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('settings.teammate-attributes.update-error'),
      );
    }

    this.isSaving = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::Teams::WfmAttributeAssigner': typeof WfmAttributeAssigner;
    'settings/teams/wfm-attribute-assigner': typeof WfmAttributeAssigner;
  }
}
